import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import styles from '../styles.module.css';
import { statusContainer, TurbineForm } from './turbineForm';
import { TurbineDetail } from './types';
import {
  parseTurbineSchema,
  editTurbineSchema,
  modelOptions,
  turbineTypeOptions
} from './utils';
import { AdminDeleteSection } from '../UtilsContainers/delete';
import { AdminDetailHeader } from '../UtilsContainers/header';
import ConfirmDeleteIcon from '@/assets/icons/confirm-delete.svg?react';
import DeleteIcon from '@/assets/icons/delete.svg?react';
import ConfirmIconSuccess from '@/assets/icons/success-icon.svg?react';
import TurbineIcon from '@/assets/icons/turbine.svg?react';
import { AdminCardButtons } from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import { Button } from '@/components/Form/Button';
import { PermissionsList } from '@/constants/permissionsList';
import useModal from '@/context/ModalContext/useModal';
import { useToast } from '@/context/ToastContext';
import {
  useCreateUpdateTurbine,
  useDeleteTurbine,
  useGetTurbineDetails,
  type TurbineFormData
} from '@/hooks/services/Turbines';
import { TURBINE_IS_ACTIVE } from '@/solaris-lens-common-lib/src/constants/turbines';
import { RootState } from '@/store';
import { getTurbinesPermissions } from '@/utils/adminToolPermissions';

const TurbineDetails = () => {
  const { turbineId } = useParams<{ turbineId: string }>();
  const auditableElements = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.permissions
  ) as PermissionsList;

  const userGroup = useSelector(
    (state: RootState) => state.auditableElements?.authorizations?.userGroup
  );

  const { canEdit, canDelete } = getTurbinesPermissions(
    auditableElements,
    userGroup
  );

  const [showEditTurbine, setShowEditTurbine] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});
  const navigate = useNavigate();
  const { updateModalContent, closeModal, showTypedModal } = useModal();
  const { showToast } = useToast();

  const {
    data: turbineResponse,
    isLoading: loading,
    error: queryError,
    refetch
  } = useGetTurbineDetails(turbineId || '');
  const { mutate: saveTurbine } = useCreateUpdateTurbine('edit');
  const { mutate: deleteTurbine } = useDeleteTurbine();

  const [formData, setFormData] = useState<TurbineFormData>({
    id: '',
    serialNumber: '',
    name: '',
    brand: '',
    model: modelOptions[0],
    turbineType: turbineTypeOptions[0],
    maxCapacity: '',
    manufacturingCountry: '',
    manufacturingYear: new Date().getFullYear().toString(),
    acquisitionYear: new Date().getFullYear().toString(),
    status: TURBINE_IS_ACTIVE.ACTIVE as 'Active',
    isActive: true
  });

  useEffect(() => {
    const turbineData = turbineResponse as { data: TurbineDetail; meta: any };
    if (turbineData) {
      setFormData({
        id: turbineData.data.id,
        serialNumber: turbineData.data.serialNumber,
        name: turbineData.data.name,
        brand: turbineData.data.brand,
        model: turbineData.data.subType,
        turbineType: turbineData.data.turbineType,
        maxCapacity: turbineData.data.maxCapacityMW,
        manufacturingCountry: turbineData.data.manufacturingCountry,
        manufacturingYear: turbineData.data.manufacturingYear,
        acquisitionYear: new Date(turbineData.data.createdAt)
          .getFullYear()
          .toString(),
        status: (turbineData.data.isActive
          ? TURBINE_IS_ACTIVE.ACTIVE
          : TURBINE_IS_ACTIVE.INACTIVE) as 'Active' | 'Inactive',
        isActive: turbineData.data.isActive
      });
    }
  }, [turbineResponse]);

  const error = queryError ? (queryError as Error).message : null;

  const handleSave = () => {
    const dataToSave = {
      ...formData,
      manufacturingYear:
        formData.manufacturingYear || new Date().getFullYear().toString()
    };

    const parsed = editTurbineSchema.safeParse(dataToSave);

    if (!parsed.success) {
      const errs = parseTurbineSchema(parsed);
      setFormErrors(errs);
    } else {
      setFormErrors({});
      saveTurbine(dataToSave, {
        onSuccess: () => {
          showTypedModal({
            type: 'info',
            title: 'Turbine was successfully updated',
            icon: ConfirmIconSuccess
          });
          setTimeout(() => {
            closeModal();
            setShowEditTurbine(false);
            refetch();
          }, 1500);
        },
        onError: error => {
          console.error('Error saving turbine:', error);
        }
      });
    }
  };

  const handleChangeFormData = (field: keyof TurbineFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (error) {
      showToast(error || 'An error occurred while loading turbine details', {
        type: 'error',
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => refetch()
      });
    }
  }, [error]);

  if (loading) {
    return <ContainerLoader />;
  }

  const onEditTurbine = () => {
    setShowEditTurbine(prev => !prev);
  };

  const handleDelete = () => {
    deleteTurbine(formData.id as string, {
      onSuccess: () => {
        showTypedModal({
          type: 'info',
          title: 'Turbine was successfully deleted',
          icon: ConfirmDeleteIcon
        });
        setTimeout(() => {
          closeModal();
          navigate('/admin-tool/turbines');
        }, 1000);
      },
      onError: error => {
        console.error('Error deleting turbine:', error);
      }
    });
  };

  const handleDeleteConfirmation = () => {
    updateModalContent(null, {
      modalTitle: 'Delete',
      modalIcon: DeleteIcon,
      modalButtons: [
        {
          label: 'CANCEL',
          onClick: () => {
            closeModal();
          },
          variant: 'cancelRed'
        },
        {
          label: 'DELETE PERMANENTLY',
          onClick: () => {
            handleDelete();
          },
          variant: 'okRed'
        }
      ],
      alignButtons: 'center',
      contentTitle: 'You are about to delete',
      contentSubtitle: `Turbine ${formData.name}`,
      contentDescription: (
        <div className={styles.contentDescription}>
          <span>This action can not be undone.</span>
          <br />
          <span>Are you sure you want to proceed?</span>
        </div>
      )
    });
  };

  return (
    <div className={styles.adminWrapper}>
      <div className={styles.adminContainer}>
        <AdminDetailHeader
          title="Turbine Details"
          icon={TurbineIcon}
          isEditing={showEditTurbine}
          onEditClick={canEdit ? onEditTurbine : undefined}
          editButtonLabel="EDIT"
          right={statusContainer({
            showEditTurbine,
            formData,
            handleChangeFormData
          })}
        />

        <TurbineForm
          formData={formData}
          handleChangeFormData={handleChangeFormData}
          showEditTurbine={showEditTurbine}
          formErrors={formErrors}
        />
      </div>

      {showEditTurbine && canDelete && (
        <AdminDeleteSection
          title="Delete Turbine"
          onDeleteClick={handleDeleteConfirmation}
          text="This action can not be undone"
          buttonLabel="DELETE TURBINE PERMANENTLY"
        />
      )}

      {showEditTurbine && (
        <AdminCardButtons>
          <Button
            onClick={() => {
              setShowEditTurbine(false);
              refetch();
            }}
            color="blue"
            solid={false}
            label="CANCEL"
            size="large"
          />
          <Button
            onClick={handleSave}
            color="blue"
            label="SAVE"
            size="large"
            loading={loading}
          />
        </AdminCardButtons>
      )}
    </div>
  );
};

export default TurbineDetails;
